import {Component, Input, Output, EventEmitter, OnInit, SimpleChanges, OnChanges} from '@angular/core';

@Component({
  selector: 'app-local-paginator',
  templateUrl: './local-paginator.component.html',
  styleUrls: ['./local-paginator.component.scss']
})
export class LocalPaginatorComponent implements OnInit, OnChanges {

  /**
   * Items length
   */
  @Input() itemsLength: number;
  /**
   * Emits Changes when paginator changes
   */
  @Output() changePage = new EventEmitter<any>(true);
  /**
   * The currentPage
   */
  @Input() initialPage = 1;
  /**
   * Page size
   */
  @Input() pageSize: number[] = [20, 50, 100];
  /**
   * Number of pages displayed in paginator
   */
  @Input() displayedPages = 3;

  /*
  The selected page size
   */
  selectedPageSize: number;
  /*
  The pages generated by items length
   */
  pages: number[];
  /*
  The max number of pages
   */
  maxPages: number;
  /*
  An array with start index, end index and items
   */
  pager: any = {};
  /*
  Hides the paginator
   */
  hideEntrySelection: false;
  /*
  Page start index
   */
  startIndex: number;
  /*
  End start index
   */
  endIndex: number;


  ngOnInit() {
    this.calculatePages(this.itemsLength);
  }

  ngOnChanges(changes: SimpleChanges) {
    this.refresh();
  }

  /**
   * Sets the initial page and selected page size
   * @param page
   * @param pageSize
   * @return an object with start index, end index and pageItems
   */
  paginate(page: number, pageSize: number) {
    this.initialPage = page;
    this.selectedPageSize = pageSize;
    return {
      startIndex: (this.initialPage - 1) * this.selectedPageSize,
      endIndex: ((this.initialPage - 1) * this.selectedPageSize) + this.selectedPageSize,
    };
  }

  /**
   * Sets the selected page
   * @param page
   * @private
   */
  private setPage(page: number) {
    this.pager = this.paginate(page, this.selectedPageSize);
    this.changePage.emit([this.pager.startIndex, this.pager.endIndex]);
  }

  /**
   * Calculates pages on added element or page size changes
   */
  calculatePages(length) {
    this.itemsLength = length;
    if (!this.selectedPageSize) {
      this.selectedPageSize = this.pageSize[0];
    }
    this.maxPages = Math.ceil(this.itemsLength / this.selectedPageSize);
    if (this.maxPages < 1) {
      this.maxPages = 1;
    }
    this.pages = Array.from({length: this.maxPages}, (_, i) => i + 1);
    // set page if items array isn't empty
    if (this.itemsLength) {
      this.setPage(this.initialPage);
    }
    this.onchangePage();
  }

  onchangePage() {
    let start;
    let end;
    if (this.maxPages <= this.displayedPages) {
      start = 0;
      end = this.maxPages;
    } else {
      const maxPagesBeforeCurrentPage = Math.floor(this.displayedPages / 2);
      const maxPagesAfterCurrentPage = Math.ceil(this.displayedPages / 2) - 1;
      if (this.initialPage <= maxPagesBeforeCurrentPage) {
        start = 0;
        end = this.displayedPages;
      } else if (this.initialPage >= this.maxPages) {
        start = this.maxPages - this.displayedPages;
        end = this.maxPages;
      } else if (this.initialPage + maxPagesAfterCurrentPage >= this.maxPages) {
        start = this.initialPage - maxPagesBeforeCurrentPage - 1;
        end = this.initialPage + maxPagesAfterCurrentPage;
      } else {
        start = this.initialPage - maxPagesBeforeCurrentPage - 1;
        end = this.initialPage + maxPagesAfterCurrentPage;
      }
    }
    this.startIndex = start;
    this.endIndex = end;
  }

  onFirstPageClicked() {
    this.setPage(1);
    this.onchangePage();
  }

  onPreviousPageClicked() {
    this.setPage(this.initialPage - 1);
    this.onchangePage();
  }

  onNextPageClicked() {
    this.setPage(this.initialPage + 1);
    this.onchangePage();
  }

  onLastPageClicked() {
    this.setPage(Math.ceil(this.itemsLength / this.selectedPageSize));
    this.onchangePage();
  }

  onPageSizeChanged(event: any) {
    this.selectedPageSize = event.value;
    this.refresh();
    this.onFirstPageClicked();
  }

  onPageNumberClicked(pageNumber: number) {
    this.setPage(pageNumber);
    this.onchangePage();
  }

  /**
   * Refreshes the maximum pages depending on page size and set pages
   */
  refresh() {
    this.maxPages = Math.ceil(this.itemsLength / this.selectedPageSize);
    if (this.maxPages < 1) {
      this.maxPages = 1;
    }
    this.pages = Array.from({length: this.maxPages}, (_, i) => i + 1);
    this.setPage(this.initialPage);
  }

  /**
   * Returns elements on selected page
   */
  getElementsNumber() {
    if (this.maxPages === 1) {
      return this.itemsLength;
    }
    if (this.initialPage !== this.maxPages){
      return this.selectedPageSize;
    }
    return this.itemsLength % this.selectedPageSize;
  }
}
